<template>
  <div
    :id="`item-${item.id}`"
    :class="[
      'menu-item mb-3',
      { 'menu-view': $route.path === '/menu' },
      { 'out-of-stock': isOutOfStock },
      { oversold: isOversold },
    ]"
    ><v-row
      dense
      :class="[
        'align-items-center item-row',
        { 'in-cart': Number(quantity) > 0 },
        { 'low-stock': lowStockWarning },
        { highlight: isHighlighted },
      ]"
    >
      <v-col class="align-self-center">
        <span :class="['item-name']">{{ item.name }}</span>
        <div class="item-desc" v-if="item.description">{{
          item.description
        }}</div>
      </v-col>
      <v-col
        class="qnty-col px-2"
        cols="2"
        sm="2"
        md="2"
        v-if="isOutOfStock || isNewOrderPage"
      >
        <div v-if="isOutOfStock" class="oos-label">{{ stockMessage }}</div>
        <template v-else-if="isNewOrderPage">
          <div v-if="!quantity || isSmOrLess" class="text-center mb-2">
            <v-btn
              small
              color="blue"
              outlined
              :disabled="quantity >= itemStock"
              @click="plusSignAdd"
              icon
            >
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>
          <v-text-field
            v-if="getAvailabilities.length && quantity"
            v-model="quantity"
            ref="quantity"
            type="Number"
            dense
            hide-details="auto"
            reverse
            min="0"
            :class="[
              'item-quantity mt-0',
              { large: _includes(['sm', 'xs'], $mq) },
            ]"
            :placeholder="isSmOrLess ? '0' : ''"
            :disabled="getPlacingOrder || soldOut || !getAvailabilities.length"
            :error="oversold"
            :error-messages="oversold ? `Only ${itemStock} available.` : ''"
            :max="itemStock"
            :rules="[
              lessThanOrEqualToStock,
              ValidationRules.unsignedNumberRule,
            ]"
          />
          <v-label v-if="stockMessage && !oversold">{{ stockMessage }}</v-label>
        </template>
      </v-col>
      <MenuItemPrice :item="item" :price="price" />
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ValidationRules from '@/constants/ValidationRules'
import { GuestRoutes } from '@/constants/RouteNames'

import MenuItemPrice from '@/components/MenuItemPrice'
import Item from '@/models/ItemModel'
import CartItem from '@/models/CartItemModel'

import _get from 'lodash/get'
import _includes from 'lodash/includes'

export default {
  name: 'MenuItem',
  components: {
    MenuItemPrice,
  },
  props: {
    listPosition: {
      type: Number,
      default: 0,
    },
    itemId: {
      type: [String, Number],
      required: true,
      default: null,
    },
  },

  methods: {
    ...mapActions('cart', ['addCartItem', 'removeCartItem']),
    _includes,
    plusSignAdd() {
      this.quantity =
        this.quantity < this.itemStock ? this.quantity + 1 : this.quantity

      this.$nextTick(() => {
        this.$refs.quantity.focus()
      })
    },
  },
  mounted() {
    if (this.quantityFromCart !== 0) {
      this.quantity = this.quantityFromCart
    }
  },
  data() {
    return {
      quantity: null,
      oversold: false,
      ValidationRules,
      lessThanOrEqualToStock: (v) => {
        if (!v) return true
        else return this.stockRemaining >= 0
      },
    }
  },
  computed: {
    ...mapGetters('shop', ['getLowStockWarningLevel']),
    ...mapGetters('itemAndSection', ['getItemById']),
    ...mapGetters('availabilities', ['getAvailabilities']),
    ...mapGetters('cart', [
      'getHighlightedItemId',
      'getCartItemById',
      'getOversoldItemById',
      'getPlacingOrder',
    ]),
    item() {
      return Item.find(this.itemId)
    },
    cartItem() {
      return CartItem.query()
        .where('item_id', this.itemId)
        .first()
    },
    isNewOrderPage() {
      return this.$route.name === GuestRoutes.NEW_ORDER
    }, // isNewOrderPage
    numColsName() {
      return this.isNewOrderPage ? 6 : 'auto'
    },
    isSmOrLess() {
      return _includes(['sm', 'xs'], this.$mq)
    },
    soldOut() {
      return _get(this.item, `sold_out`, false)
    }, // soldOut
    price() {
      return this.item.price_usd
    }, // price
    priceInCents() {
      return this.item.price
    }, // price
    itemStock() {
      if (this.unlimitedStock) return Infinity
      return _get(this.item, 'stock', Infinity)
    }, // itemStock

    stockRemaining() {
      return this.itemStock - this.quantity
    }, // stockRemaining

    lowStockWarning() {
      return this.stockRemaining <= this.getLowStockWarningLevel
    }, // lowStockWarning

    isRemainingStockInCart() {
      return this.quantity && this.quantity >= this.itemStock
    }, // isRemainingStockInCart

    isOutOfStock() {
      return (!this.unlimitedStock && this.itemStock < 1) || this.soldOut
    }, // isOutOfStock

    unlimitedStock() {
      return _get(this.item, 'unlimited', false)
    }, // unlimitedStock

    stockMessage() {
      if (this.isOutOfStock) {
        return 'Sold Out'
      }

      if (this.lowStockWarning) {
        return `Only ${this.itemStock} left`
      }
      return ''
    }, // stockMessage

    isHighlighted() {
      return this.getHighlightedItemId === this.item.id
    }, // isHighlighted

    isOversold() {
      return _get(this, 'cartItem.oversold', false)
    }, // isOversold

    quantityFromCart() {
      return _get(this, 'cartItem.quantity', 0)
    }, // quantityFromCart
  }, // computed
  watch: {
    quantity(quantity) {
      if (quantity > 0) {
        this.addCartItem({
          ...this.item,
          quantity,
          listPosition: this.listPosition,
          oversold: 0,
        })
        this.oversold = false
      } else {
        this.removeCartItem(this.item)
      }
    }, // quantity()

    quantityFromCart(quantityFromCart) {
      if (quantityFromCart !== this.quantity) {
        this.quantity = quantityFromCart
      }
    }, // quantityFromCart

    itemStock(newStock, oldStock) {
      this.oversold = oldStock > newStock
    }, // itemStock()
  },
}
</script>

<style lang="scss">
.menu-item {
  transition: background-color 0.7s ease;
  .item-name {
    line-height: 1;
  }
  &.out-of-stock {
    position: relative;
    &,
    & .item-unit {
      color: lighten(black, 60);
      // font-style: italic;
    }
    & .item-desc {
      color: lighten(black, 75);
    }
    .oos-label {
      opacity: 0.8;
      font-size: 1rem;
      color: #0d78cd;
      font-family: williams-caslon-text, serif !important;
      &.absolute {
        position: absolute;
        right: 0;
        top: 0;
      }
    }
  }
  &.oversold {
    .item-name {
      padding-bottom: 1px;
      border-bottom: 4px solid #da4848;
    }
  }
}
.menu-view:not(:last-of-type) {
  border-bottom: 1px dotted lighten(black, 85);
}
.menu-view:first-of-type {
  // border-top: 1px dotted lighten(black, 85);
}
.item-quantity.large {
  font-size: 24px;
}
.item-desc {
  white-space: pre-wrap;
  font-style: italic;
  color: lighten(black, 40);
}

.item-row {
  transition: background-color 0.7s ease;

  &.in-cart {
    background-color: #e5ffeb;
  }
  &.highlight {
    // border-top: 4px solid lighten(red, 10);
    background-color: #edd1ff;
  }
}
</style>
